import { Button, Grid, Menu, MenuItem, Tooltip, Typography, Divider, Box } from '@mui/material';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { postSaveOrder, setReviewOrder } from '../../../../api/orders';
import { clearTempOrderList, getPendingOrderInfo, getPendingOrders } from '../../../../redux/slices/orders';
import { useDispatch, useSelector } from '../../../../redux/store';
import { AwaitButton } from '../../../../reusable-components/await-button';
import Iconify from '../../../../reusable-components/iconify';
import { ScopeGuard, useScopeCheck } from '../../../../reusable-components/scopes';
import { useSnackbar } from '../../../../reusable-components/snackbar';
import { fDate, timestamp } from '../../../../utils/formatTime';
import NewItemDialog from '../../admin/catalog/NewItemDialog';
import DownloadOrderDropdown from './DownloadOrderDropdown';
import OrderTotal from './OrderTotal';
import DeleteOrderModal from '../DeleteOrderModal';
import ConfirmSaveOrderDialog from './ConfirmSaveOrderDialog';
import DropdownAction from '../../../../reusable-components/dropdown-action/DropdownAction';
import DropdownExpandableAction from '../../../../reusable-components/dropdown-action/DropdownExpandableAction';
import { markMetricRemoved, NotificationMetricType } from '../../../../redux/slices/notifications';

CustomToolbar.propTypes = {
  setSelected: PropTypes.func,
  setSplitOrder: PropTypes.func,
  addItemCategoryId: PropTypes.number,
  setAddItemCategoryId: PropTypes.func,
  setOpenProcessDialog: PropTypes.func,
  activeRows: PropTypes.array,
  goToNextOrder: PropTypes.func,
  handleVendorFilterClick: PropTypes.func,
  selectedVendor: PropTypes.object,
  updatedCartItems: PropTypes.array,
  cartCheckFunction: PropTypes.func,
  isLoading: PropTypes.bool,
  fetchOrderLogData: PropTypes.func,
  setOrderLogUpdated: PropTypes.func,
};

export default function CustomToolbar({
  setSelected,
  setSplitOrder,
  addItemCategoryId,
  setAddItemCategoryId,
  setOpenProcessDialog,
  activeRows,
  goToNextOrder,
  handleVendorFilterClick,
  selectedVendor,
  updatedCartItems,
  cartCheckFunction,
  isLoading,
  fetchOrderLogData,
  setOrderLogUpdated,
}) {
  const [searchParams] = useSearchParams();
  const filter = searchParams.get('filter');
  const hasAccess = useScopeCheck(['Edit-Order', 'Submit-Order'], true);
  const canOrderFromCatalog = useScopeCheck(['Order-ByCatalog'], true);
  const dispatch = useDispatch();
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { orderById, cartDialog } = useSelector((state) => state.orders || {});
  const [actionsAnchorEl, setActionsAnchorEl] = useState(null);
  const {
    unsavedChanges,
    items = [],
    facilityId,
    orderAgoraCategories = [],
    agoraSubcategories = [],
    vendors = [],
    orderNumber,
    orderDelay,
    importedOrder,
    orderReviews,
    requiredReviewPreference,
  } = orderById[id] || {};
  const { user } = useSelector((state) => state.user);
  const orderQueue = JSON.parse(localStorage.getItem('selectedOrders'));
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const userId = searchParams.get('userId');
  const back = searchParams.get('back');
  const vendor = searchParams.get('vendor');
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [isConfirmSaveDialogOpen, setIsConfirmSaveDialogOpen] = useState(false);
  const [isDownloadExpanded, setIsDownloadExpanded] = useState(false);

  const handleSetReviewOrder = async (id) => {
    setLoading(true);
    const response = await setReviewOrder(id);
    if (response.status === 200) {
      setOrderLogUpdated(true);
      dispatch(getPendingOrders());
      dispatch(getPendingOrderInfo(id));

      enqueueSnackbar('Order has been reviewed', { variant: 'success' });
    } else {
      enqueueSnackbar('Error reviewing order', { variant: 'error' });
    }
    setLoading(false);
  };

  const saveOrderFunction = async (isSaveAndProcess) => {
    setIsSaving(true);
    const punchoutItemQuantityChanged = orderById[id]?.items?.some((item) => isPunchoutItemQtyModified(item));
    if (punchoutItemQuantityChanged) setIsConfirmSaveDialogOpen(true);
    else await confirmSaveOrder(isSaveAndProcess);
  };

  const confirmSaveOrder = async (isSaveAndProcess) => {
    const response = await postSaveOrder({orderId: id, items});
    if (response.status === 200) {
      setOrderLogUpdated(true);
      dispatch(clearTempOrderList(id));
      dispatch(getPendingOrderInfo(id, false));
      setSelected([]);
      setSplitOrder(false);
      setAddItemCategoryId(null);
      cartCheckFunction(false);
      enqueueSnackbar('Order has been updated!', { variant: 'success' });
      fetchOrderLogData();
      if (isSaveAndProcess) setOpenProcessDialog(true);
    } else {
      enqueueSnackbar('Error saving order', { variant: 'error' });
    }

    setIsSaving(false);
  };

  const duplicateItem =
    items &&
    Object.entries(
      Object.groupBy(
        items
          .filter((item) => !item.deleted && item.approved && item.itemId !== -1)
          .map((item) => ({ vendorItemId: item.vendorItemId, vendorId: item.vendorId, uomType: item.uomType })),
        (res) => res.vendorItemId
      )
    ).find((res) => res[1].length > 1);
  const duplicateItemError = duplicateItem
    ? `There can't be at two of the same sku/uom for a vendor. Remove/unapprove one (sku: ${duplicateItem[0]}).`
    : null;
  const itemError =
    items === null || items.length === 0
      ? 'There must be at least one item.'
      : items.some(
          (item) => !item.deleted && item.approved === true && (!item.agoraCategory?.id || !item.agoraSubcategory?.id)
        )
      ? 'Make sure all approved items have a category and subcategory.'
      : duplicateItemError;
  
  const awaitingReview =
    (orderReviews?.some((r) => r.required) &&
      requiredReviewPreference === 'Any' &&
      !orderReviews?.some((r) => r.required && r.reviewed)) ||
    (requiredReviewPreference !== 'Any' && orderReviews?.some((r) => r.required && !r.reviewed));

  const sendDate = orderDelay ? new Date(orderDelay.sendOrderAt) : null;
  const isPast = sendDate ? sendDate <= new Date() : false;

  const handleCancelSave = () => {
    setIsConfirmSaveDialogOpen(false);
    setIsSaving(false);
  };

  const isPunchoutItemQtyModified = (item) =>
    item.itemId === -1 && item.origQty && item.origQty !== item.quantity && item.originalQuantity !== item.quantity;

  return (
    <>
      <Grid container direction="row" justifyContent="space-between" alignItems="flex-end" sx={{ mb: 1 }}>
        <Grid item>
          <Button
            variant="text"
            onClick={() => {
              if (back && back === 'vendorOrders') {
                navigate(`/dashboard/admin/vendors?tab=9&vendor=${vendor}`);
              } else if (back && back === 'userOrders' && userId) {
                navigate(`/dashboard/admin/users/${userId}?tab=4`);
              } else if (filter === 'rerun') {
                const params = new URLSearchParams(searchParams);
                params.set('filter', 'previous');
                if (params.get('rerunFromVendorView') !== '1') {
                  params.delete('vendorId');
                }
                params.delete('rerunFromVendorView');
                navigate(`/dashboard/orders/${id}?${params.toString()}`);
              }
              else navigate(`/dashboard/orders?filter=pending`);
            }}
            startIcon={<Iconify icon="mingcute:left-fill" width={24} height={24} sx={{ mx: -1 }} />}
            sx={{
              color: '#454F5B',
              fontWeight: 'bold',
            }}
          >
            {filter === 'rerun'
              ? 'Back to completed order'
              : back && back === 'vendorOrders'
              ? 'Back to vendor orders'
              : back && back === 'userOrders'
              ? 'Back to user orders'
              : ' Back to pending orders'}
          </Button>
        </Grid>
        <Grid item>
          <Typography variant="body2" sx={{ fontWeight: 'bold', display: 'flex' }} color="#208ee0">
            {orderById[id]?.combinedOrder && (
              <Tooltip
                title={orderById[id]?.orderMessages?.map((msg, idx) => (
                  <Box key={idx}>{msg}</Box>
                ))}
                placement="top"
                arrow
              >
                <Iconify icon="mdi:call-merge" sx={{ mr: 0.5 }} />
              </Tooltip>
            )}
            Order #{orderNumber || id}
          </Typography>
        </Grid>
        <Grid item />
      </Grid>

      <Grid container direction="row" spacing={1}>
        {addItemCategoryId ? (
          <>
            <Grid item>
              <NewItemDialog
                agoraCat={orderAgoraCategories}
                agoraSubCat={agoraSubcategories}
                agoraVendors={vendors.map((vendor) => ({ value: vendor.id, label: vendor.name }))}
                addToOrderList={{
                  facilityId,
                  categoryId: addItemCategoryId,
                }}
                orderId={id}
                buttonProps={{ sx: {} }}
              />
            </Grid>
            <Grid item>
              <OrderTotal
                activeRows={activeRows}
                handleVendorFilterClick={handleVendorFilterClick}
                selectedVendor={selectedVendor}
              />
            </Grid>
          </>
        ) : (
          <>
            {!importedOrder && (
              <ScopeGuard scopes={['Edit-Order']} allowAdmin>
                <Grid item>
                  <Button
                    color="secondary"
                    size="small"
                    variant="outlined"
                    startIcon={<Iconify icon="eva:plus-outline" />}
                    onClick={(event) => setMenuAnchorEl(event.currentTarget)}
                  >
                    Add items
                  </Button>
                </Grid>
              </ScopeGuard>
            )}
            <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={() => setMenuAnchorEl(null)}>
              {[...(orderAgoraCategories || []), ...(canOrderFromCatalog ? [{ id: -1, name: 'Catalog' }] : [])]
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((category) => (
                  <MenuItem
                    key={category.id}
                    onClick={() => {
                      setMenuAnchorEl(null);
                      setAddItemCategoryId(category.id);
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {category.name}
                    </Typography>
                  </MenuItem>
                ))}
            </Menu>
            <ScopeGuard scopes={['Cart-Check', 'Edit-Order']} allowAdmin>
              <Grid item>
                <AwaitButton
                  color={updatedCartItems?.length === 0 && cartDialog ? 'success' : 'secondary'}
                  size="small"
                  variant="outlined"
                  startIcon={<Iconify icon="ph:list-magnifying-glass" />}
                  onClick={() => cartCheckFunction(true)}
                  loading={isLoading}
                >
                  Check Order
                </AwaitButton>
              </Grid>
            </ScopeGuard>
            <Grid item>
              <OrderTotal
                activeRows={activeRows}
                handleVendorFilterClick={handleVendorFilterClick}
                selectedVendor={selectedVendor}
              />
            </Grid>
            {orderReviews?.find((o) => o.reviewerEmail === user.email)?.reviewed === false && (
              <Grid item>
                <AwaitButton
                  color="primary"
                  size="small"
                  variant="outlined"
                  loading={loading}
                  onClick={() => handleSetReviewOrder(id)}
                >
                  Review order
                </AwaitButton>
              </Grid>
            )}
            {filter !== 'rerun' && unsavedChanges && !addItemCategoryId && hasAccess && (
              <Grid item>
                <Button
                  color="error"
                  onClick={() => {
                    window.location.reload();
                  }}
                  variant="outlined"
                  size="small"
                  sx={{ mr: 1 }}
                  endIcon={<Iconify icon={'mdi:reload'} />}
                >
                  Cancel
                </Button>
                <Tooltip title={duplicateItemError ?? ''}>
                  <span>
                    <AwaitButton
                      disabled={!!(isSaving || duplicateItemError)}
                      color="primary"
                      size="small"
                      variant="contained"
                      onClick={async () => saveOrderFunction()}
                    >
                      Save changes
                    </AwaitButton>
                  </span>
                </Tooltip>
              </Grid>
            )}
            <ScopeGuard scopes={['Submit-Order']} allowAdmin>
              <Tooltip
                placement="top"
                arrow
                title={
                  itemError ||
                  (orderDelay ? (
                    <div>
                      Order was set on a timer by {orderDelay.addedBy} on {timestamp(orderDelay.dateAdded)}
                      <br />
                      Order {isPast ? 'was' : 'will be'} sent out on
                      {fDate(orderDelay.sendOrderAt, 'MM/dd/yyyy hh:mm a')}
                    </div>
                  ) : (
                    ''
                  ))
                }
              >
                <Grid item>
                  <Tooltip
                    title={
                      awaitingReview
                        ? requiredReviewPreference === 'Any'
                          ? 'Order is waiting on a required reviewer to review'
                          : 'Order is waiting on all required reviewers to review'
                        : ''
                    }
                  >
                    <span>
                      <AwaitButton
                        color="primary"
                        size="small"
                        variant="contained"
                        disabled={!!(isSaving || itemError || awaitingReview)}
                        onClick={async () => {
                          if (unsavedChanges && filter !== 'rerun') await saveOrderFunction(true);
                          else setOpenProcessDialog(true);
                        }}
                      >
                        {unsavedChanges && filter !== 'rerun' ? 'Save & Process' : 'Process'}
                        {orderQueue?.length > -1 && ' & next'}
                      </AwaitButton>
                    </span>
                  </Tooltip>
                </Grid>
              </Tooltip>
              {orderQueue?.length > -1 && (
                <Grid item>
                  <Button
                    sx={{ textDecoration: 'underline' }}
                    color="secondary"
                    onClick={() => goToNextOrder()}
                    size="small"
                  >
                    Next
                    {orderQueue?.length ? ` (${orderQueue?.length})` : ''}
                  </Button>
                </Grid>
              )}
            </ScopeGuard>
            <Grid item>
              <Tooltip title="More actions">
                <Button
                  variant="outlined"
                  color="primary"
                  sx={{
                    px: 1,
                    minWidth: 0,
                  }}
                  onClick={(e) => setActionsAnchorEl(e.currentTarget)}
                >
                  <Iconify icon="mdi:dots-horizontal" />
                </Button>
              </Tooltip>
              <Menu
                anchorEl={actionsAnchorEl}
                open={Boolean(actionsAnchorEl)}
                onClose={() => {
                  setActionsAnchorEl(null);
                  setIsDownloadExpanded(false);
                }}
                slotProps={{
                  paper: { sx: { p: 1, minWidth: 220, maxWidth: 280 } },
                  list: { sx: { display: 'flex', flexDirection: 'column', p: 0 } },
                }}
              >
                <DropdownExpandableAction
                  icon="material-symbols:download"
                  panel={<DownloadOrderDropdown setAnchorEl={setActionsAnchorEl} />}
                  onExpandedChange={setIsDownloadExpanded}
                >
                  Download
                </DropdownExpandableAction>

                {isDownloadExpanded && <Divider sx={{ my: 0.5, mx: 2 }} />}

                <ScopeGuard scopes={['Edit-Order']} allowAdmin>
                  {!items?.some((i) => i.itemId === -1) && (
                    <MenuItem
                      disableRipple
                      disableGutters
                      sx={{
                        p: 0,
                        minHeight: 'unset',
                        '&:hover': {
                          bgcolor: 'transparent',
                        },
                      }}
                      onClick={() => {
                        setActionsAnchorEl(null);
                        setSplitOrder(true);
                      }}
                    >
                      <DropdownAction badgeIcon="lucide:split">Split Order</DropdownAction>
                    </MenuItem>
                  )}

                  <Divider sx={{ my: 0.5, mx: 2 }} />

                  <MenuItem
                    disableRipple
                    disableGutters
                    sx={{
                      p: 0,
                      minHeight: 'unset',
                      '&:hover': {
                        bgcolor: 'transparent',
                      },
                    }}
                    disabled={isSaving}
                    onClick={() => {
                      setActionsAnchorEl(null);
                      setIsConfirmDialogOpen(true);
                    }}
                  >
                    <DropdownAction
                      badgeIcon="ic:outline-delete"
                      iconColor="error.main"
                      iconBgColor="#FDEDED"
                      textColor="error.main"
                    >
                      Delete Order
                    </DropdownAction>
                  </MenuItem>
                </ScopeGuard>
              </Menu>
            </Grid>
          </>
        )}
      </Grid>
      <DeleteOrderModal
        content={`Are you sure you want to delete ${orderNumber}?`}
        rowId={id}
        disableRipple
        action={() => {
          dispatch(markMetricRemoved(NotificationMetricType.OrdersSubmitted, id));
          dispatch(markMetricRemoved(NotificationMetricType.OrdersNeedApproval, id));
          navigate('/dashboard/orders?filter=pending');
        }}
        openConfirm={isConfirmDialogOpen}
        setOpenConfirm={setIsConfirmDialogOpen}
      />
      <ConfirmSaveOrderDialog
        isConfirmSaveDialogOpen={isConfirmSaveDialogOpen}
        setIsConfirmSaveDialogOpen={setIsConfirmSaveDialogOpen}
        // an item will only get the property `origQty` if it is updated
        itemsModified={orderById[id]?.items?.filter((item) => isPunchoutItemQtyModified(item))}
        handleCancelSave={handleCancelSave}
        handleSave={confirmSaveOrder}
      />
    </>
  );
}
